import { useMemo } from 'react';
import {
    generateMockMetricData,
    generateMockChartData,
    generateMockTableData,
    generateMockInsightData,
} from './mockData';
import { generateMockClassEngagementData } from './mockEngagementData';
import { dataApi, pythonApi } from '../../config/axios';
import {
    getWidgetCache,
    updateWidgetCacheInReport,
    getWidgetCacheTimestamp,
    generateCacheKey as generateWidgetCacheKey,
} from './cacheUtils';
import { CustomColors } from 'assets/customisedMUI';

/**
 * Merge global filters with data source filters
 * Global filters take precedence and restrict available options
 * Only merges classes, grades, and period (not cross-metric filters)
 */
function mergeFilters(globalFilters, dataSourceFilters) {
    if (!globalFilters) return dataSourceFilters || {};
    if (!dataSourceFilters) return globalFilters;

    const merged = { ...dataSourceFilters };

    // For array filters (classIds, gradeIds), ensure data source filters only include values from global filters
    if (globalFilters.classIds && merged.classIds) {
        merged.classIds = merged.classIds.filter((id) => globalFilters.classIds && globalFilters.classIds.includes(id));
    } else if (globalFilters.classIds) {
        merged.classIds = [...globalFilters.classIds];
    }

    if (globalFilters.gradeIds && merged.gradeIds) {
        merged.gradeIds = merged.gradeIds.filter((id) => globalFilters.gradeIds && globalFilters.gradeIds.includes(id));
    } else if (globalFilters.gradeIds) {
        merged.gradeIds = [...globalFilters.gradeIds];
    }

    // Merge period/date range (global takes precedence, but only if set)
    if (globalFilters.period) {
        merged.period = globalFilters.period;
        if (globalFilters.startDate) {
            merged.startDate = globalFilters.startDate;
        }
        if (globalFilters.endDate) {
            merged.endDate = globalFilters.endDate;
        }
    }

    return merged;
}

/**
 * Hook to fetch widget data
 * TODO: Replace with actual API call
 * @param config Widget configuration
 * @param globalFilters Optional global filters to merge with data source filters
 */
export function useWidgetData(config, globalFilters) {
    return useMemo(() => {
        const configWithMergedFilters = {
            ...config,
            dataSources: config.dataSources.map((ds) => ({
                ...ds,
                filters: mergeFilters(globalFilters, ds.filters),
            })),
        };

        switch (config.type) {
            case 'metric':
                return generateMockMetricData(configWithMergedFilters);
            case 'line-chart':
            case 'bar-chart':
                return generateMockChartData(configWithMergedFilters);
            case 'table':
                return generateMockTableData(configWithMergedFilters);
            case 'insight':
                return generateMockInsightData(configWithMergedFilters);
            default:
                return null;
        }
    }, [config, globalFilters]);
}

/**
 * Hook to save report
 * TODO: Replace with actual API call: POST /api/reports
 */
export function useSaveReport() {
    return async (report) => {
        try {
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);

            const annualPeriod = url.pathname.includes('planning-report')

            if (annualPeriod) return;

            const level = params.get('level')
            const levelId = params.get('levelId')
            const period = annualPeriod ? 'annual' : params.get('period')
            const interval = params.get('interval')
            const term = params.get('term')
            const year = annualPeriod ? new Date().getFullYear.toString() : params.get('year')

            const paramMap = {
                level,
                levelId,
                period,
                interval,
                term,
                year,
            }

            const requiredParams = annualPeriod
                ? ['period', 'year']
                : level === 'school' ?
                    ['level', 'period', 'interval', 'term', 'year'] :
                    ['level', 'levelId', 'period', 'interval', 'term', 'year']


            const missingParams = requiredParams.filter(
                (key) => paramMap[key] == null || paramMap[key] === ''
            )

            if (missingParams.length > 0) {
                throw new Error(
                    `[Report Template] Cannot persist template. Missing required query params: ${missingParams.join(', ')}`
                )
            }

            const queryParams = new URLSearchParams(paramMap);

            await dataApi.post(`api/dynamic-reports/report-template?${queryParams.toString()}`, report);
            console.info("Successfully saved report template")
        } catch (e) {
            console.error(`Failed to save report: ${e}`)
        }
    };
}

/**
 * Hook to load report
 * TODO: Replace with actual API call: GET /api/dynamic-reports/:id
 */
export function useLoadReport() {
    return async (reportId) => {
        const saved = localStorage.getItem(`report-${reportId}`);
        if (saved) {
            return JSON.parse(saved);
        }

        return null;
    };
}

/**
 * Fetch metric data from API
 * @param config Widget configuration
 * @param globalFilters Optional global filters to merge with data source filters
 * @param includeComparison Whether to include comparison/trend data
 */
export async function fetchUnifiedAI(requestBody) {
    try {
        const response = await pythonApi.post('/api/agent/handle-unified', requestBody);
        return response?.data
    } catch (error) {
        console.error('Error fetching AI response (chatbot):', error);
        throw error;
    }
}


/**
 * Fetch metric data from API
 * @param config Widget configuration
 * @param globalFilters Optional global filters to merge with data source filters
 * @param includeComparison Whether to include comparison/trend data
 */
export async function fetchMetricData(config, globalFilters, includeComparison = false) {
    const metricConfig = config.config;

    const requestBody = {
        metricType: metricConfig?.metricType || 'emotional-state',
        selectedSourceId: metricConfig?.selectedSourceId,
        dateRange: metricConfig?.dateRange,
        checkInThreshold: metricConfig?.checkInThreshold,
        dataSources: config.dataSources
            .filter((ds) => ds.enabled)
            .map((ds) => ({
                sourceId: ds.sourceId,
                filters: ds.filters,
            })),
        globalFilters,
        includeComparison,
    };

    if (includeComparison && metricConfig?.comparison?.enabled) {
        requestBody.comparison = {
            period: metricConfig.comparison.period,
            startDate: metricConfig.comparison.startDate,
            endDate: metricConfig.comparison.endDate,
        };
    }

    try {
        const response = await dataApi.post('/api/dynamic-reports/metrics/query', requestBody);
        const { results } = response.data;

        return results.map((result) => ({
            value: result.value,
            trend: result.trend || '0%',
            trendDirection: result.trendDirection || 'neutral',
            count: result.count,
            total: result.total,
        }));
    } catch (error) {
        console.error('Error fetching metric data:', error);
        console.warn('Falling back to mock data');
        return config.dataSources
            .filter((ds) => ds.enabled)
            .map((ds) => {
                const sourceWidget = {
                    ...config,
                    dataSources: [{ ...ds, filters: mergeFilters(globalFilters, ds.filters) }],
                };
                return generateMockMetricData(sourceWidget);
            });
    }
}

/**
 * Fetch insight data from API using AI
 * @param config Widget configuration
 * @param globalFilters Optional global filters to determine which data to analyze
 * @param reportWidgets Optional all widgets in the report for context
 */
export async function fetchInsightData(config, globalFilters, reportWidgets) {
    const insightConfig = config.config;

    const requestBody = {
        widgetId: config.id,
        widgetType: 'insight',
        config: {
            condition: insightConfig?.condition,
            type: insightConfig?.type || 'info',
            message: insightConfig?.message,
        },
        globalFilters: globalFilters || {},
        reportContext: reportWidgets
            ? {
                widgetCount: Object.keys(reportWidgets).length,
                widgetTypes: Object.values(reportWidgets).map((w) => w.type),
            }
            : undefined,
    };

    try {
        const response = await dataApi.post('/api/dynamic-reports/', requestBody);
        const data = response.data;

        const apiMessage = data.message || data.insight;
        const configMessage = insightConfig?.message;
        const message = apiMessage || configMessage || 'No insight available';

        const finalMessage = Array.isArray(message) ? message : [message];

        return {
            message: finalMessage,
            type: data.type || insightConfig?.type || 'info',
        };
    } catch (error) {
        console.error('Error fetching insight data:', error);
        console.warn('Falling back to mock insight data');
        return generateMockInsightData(config);
    }
}

/**
 * Fetch raw State of Emotion data from API (for drill-down)
 * @param config Widget configuration
 * @param globalFilters Optional global filters to merge with data source filters
 * @returns Raw API response data
 */
export async function fetchStateOfEmotionRawData(config, globalFilters?, opts = {}) {
    const { level, levelId, period, interval, term, year } = opts;
    const queryParams = `${level ? `level=${level}` : ''}${interval ? `&interval=${interval}` : ''}${term ? `&term=${term}` : ''}${year ? `&year=${year}` : ''}${period ? `&period=${period}` : ''}${levelId ? `&levelId=${levelId}` : ''}`;

    const requestBody = {
        dataSources: config.dataSources
            .filter((ds) => ds.enabled)
            .map((ds) => ({
                sourceId: ds.sourceId,
                filters: ds.filters,
            })),
        globalFilters: globalFilters || {},
        dateRange: config.config?.dateRange,
        config: {
            ...(level && { level }),
            ...(levelId && { levelId }),
        },
        aiSummaryEnabled: config?.aiSummaryEnabled || false,
        displayMode: 'bar',
    };



    try {
        const response = await dataApi.post(`/api/dynamic-reports/state-of-emotion/?${queryParams}`, requestBody);
        const responseData = response.data;

        if (!responseData.success || !responseData.data) {
            throw new Error('Invalid API response format');
        }
        return responseData?.data;
    } catch (error) {
        console.error('Error fetching state of emotion raw data:', error);
        throw error;
    }
}

/**
 * Fetch State of Emotion data from API (with caching support)
 * @param config Widget configuration
 * @param globalFilters Optional global filters to merge with data source filters
 * @param report Optional report config for cache checking/updating
 * @param updateReportCallback Optional callback to update report with cached data
 */
export async function fetchStateOfEmotionData({
    config,
    globalFilters,
    report,
    updateReportCallback,
    forceRefresh = false,
    opts = {}
}) {
    if (report && !forceRefresh) {
        const cacheKey = generateWidgetCacheKey(config, globalFilters);
        const cachedData = getWidgetCache(report, config.id, cacheKey, report.metadata?.published || false);
        if (cachedData) {
            return cachedData;
        }
    }

    try {
        const rawData = await fetchStateOfEmotionRawData(config, globalFilters, opts);

        if (config?.aiSummaryEnabled && rawData?.summary) {
            return {
                aiSummary: rawData,
            };
        }

        const emotionalStateMap = {
            'ready-to-learn': { label: 'Ready to Learn', color: '#14B8A6' }, // Teal
            processing: { label: 'In a state of processing', color: '#EAB308' }, // Mustard
            'not-ready': { label: 'Not Ready to Learn', color: '#2196F3' }, // Blue
            'heightened-emotion': { label: 'Heightened State of Emotion', color: '#9C27B0' }, // Purple
            'emotional-overload': { label: 'Emotional Overload', color: '#F44336' },
        };

        const stateOrder = ['ready-to-learn', 'processing', 'not-ready', 'heightened-emotion', 'emotional-overload'];

        const sortedSummary = [...rawData.summary].sort((a, b) => {
            const indexA = stateOrder.indexOf(a.sourceId);
            const indexB = stateOrder.indexOf(b.sourceId);
            return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
        });

        const labels = [];
        const data = [];
        const backgroundColor = [];
        const borderColor = [];

        sortedSummary.forEach((item) => {
            const stateInfo = emotionalStateMap[item.sourceId];
            if (stateInfo) {
                labels.push(stateInfo.label);
                data.push(parseFloat(item.percentage));
                backgroundColor.push(stateInfo.color);
                borderColor.push(stateInfo.color);
            }
        });

        const chartData = {
            labels,
            datasets: [
                {
                    label: 'Emotional States',
                    data,
                    backgroundColor: backgroundColor,
                    borderColor: borderColor,
                    borderWidth: 2,
                    tension: 0,
                },
            ],
        };

        if (report && updateReportCallback) {
            const cacheKey = generateWidgetCacheKey(config, globalFilters);
            const updatedReport = updateWidgetCacheInReport(report, config.id, chartData, cacheKey);
            updateReportCallback(updatedReport);
        }

        return chartData;
    } catch (error) {
        console.error('Error fetching state of emotion data:', error);
        throw error;
    }
}

/**
 * Fetch State of Emotion by Week data from API (for readiness trend line chart)
 * @param config Widget configuration
 * @param globalFilters Optional global filters
 * @param report Optional report config for cache checking/updating
 * @param updateReportCallback Optional callback to update report with cached data
 * @param forceRefresh Whether to force refresh (bypass cache)
 * @returns ChartData formatted for line chart
 */
export async function fetchStateOfEmotionByWeekData(
    config,
    globalFilters?,
    report?,
    updateReportCallback?: (updatedReport) => void,
    forceRefresh = false, opts = {}
) {
    if (report && !forceRefresh) {
        const cacheKey = generateWidgetCacheKey(config, globalFilters);
        const cachedData = getWidgetCache(report, config.id, cacheKey, report.metadata?.published || false);
        if (cachedData) {
            const chartData = cachedData.chartData || cachedData;
            return chartData;
        }
    }

    try {
        const { level, interval, term, year, period, levelId } = opts;
        const queryParams = `${level ? `level=${level}` : ''}${interval ? `&interval=${interval}` : ''}${term ? `&term=${term}` : ''}${year ? `&year=${year}` : ''}${period ? `&period=${period}` : ''}${levelId ? `&levelId=${levelId}` : ''}`;

        const response = await dataApi.get(`/api/dynamic-reports/state-of-emotion-by-week/?${queryParams.toString()}`);

        const responseData = response.data;


        if (!responseData.success || !responseData.data) {
            throw new Error('Invalid API response format');
        }

        const weekWiseData = responseData.data.weekWiseDataForTerm || [];
        const fortnightWiseData = responseData.data.fortnightWiseDataForTerm || [];

        const apiStateToSourceId = (apiStateName) => {
            if (!apiStateName) return null;
            const normalized = apiStateName.toLowerCase().trim();

            if (normalized.includes('ready to learn') && !normalized.includes('not') && !normalized.includes('quite')) {
                return 'ready-to-learn';
            }
            if (normalized.includes('state of processing') || normalized.includes('processing')) {
                return 'processing';
            }
            if (normalized.includes('not quite ready') || (normalized.includes('not ready') && !normalized.includes('overload'))) {
                return 'not-ready';
            }
            if (normalized.includes('heightened') || normalized.includes('heightened state')) {
                return 'heightened-emotion';
            }
            if (normalized.includes('emotional overload') || normalized.includes('overload')) {
                return 'emotional-overload';
            }
            return null;
        };

        const emotionalStateMap = {
            'ready-to-learn': { label: 'Ready to Learn', color: '#14B8A6' },
            processing: { label: 'In a state of processing', color: '#EAB308' },
            'not-ready': { label: 'Not Ready to Learn', color: '#2196F3' },
            'heightened-emotion': { label: 'Heightened State of Emotion', color: '#9C27B0' },
            'emotional-overload': { label: 'Emotional Overload', color: '#F44336' },
        };

        const enabledSources = (config.dataSources || [])
            .filter((ds) => ds.enabled !== false)
            .map((ds) => ds.sourceId);

        const labels = period === 'fortnightly' ? fortnightWiseData.map((fortnight) => fortnight.fortnightLabel || `Fortnight ${fortnight.fortnightId}`) : weekWiseData.map((week) => week.weekLabel || `Week ${week.weekId}`);

        const apiStateNames = new Set();
        period === 'fortnightly' ? fortnightWiseData.forEach((fortnight) => {
            fortnight.fortnightDataByZorPercentage?.forEach((item) => {
                if (item.stateOfEmotion) {
                    apiStateNames.add(item.stateOfEmotion);
                }
            });
        }) : weekWiseData.forEach((week) => {
            week.weekDataByZorPercentage?.forEach((item) => {
                if (item.stateOfEmotion) {
                    apiStateNames.add(item.stateOfEmotion);
                }
            });
        });

        const datasets = enabledSources.map((sourceId, index) => {
            const stateInfo = emotionalStateMap[sourceId];
            if (!stateInfo) {
                console.warn(`[Readiness Trend] No state info found for sourceId: ${sourceId}`);
                return null;
            }

            const apiStateName = Array.from(apiStateNames).find((apiName) => {
                const mappedSourceId = apiStateToSourceId(apiName);
                return mappedSourceId === sourceId;
            });

            if (!apiStateName) {
                console.warn(`[Readiness Trend] No API state name found for sourceId: ${sourceId}. Available:`, Array.from(apiStateNames));
                return null;
            }

            const data = period === 'fortnightly' ? fortnightWiseData.map((fortnight) => {
                const stateData = fortnight.fortnightDataByZorPercentage?.find(
                    (item) => item.stateOfEmotion === apiStateName
                );
                return stateData?.percentage !== null && stateData?.percentage !== undefined
                    ? parseFloat(stateData.percentage)
                    : 0;
            }) : weekWiseData.map((week) => {
                const stateData = week.weekDataByZorPercentage?.find(
                    (item) => item.stateOfEmotion === apiStateName
                );

                return stateData?.percentage !== null && stateData?.percentage !== undefined
                    ? parseFloat(stateData.percentage)
                    : 0;
            });

            const hasData = data.some((value) => value > 0);
            if (!hasData) {
                console.warn(`[Readiness Trend] No data found for ${stateInfo.label} (all values are 0)`);
            }

            return {
                label: stateInfo.label,
                data,
                borderColor: stateInfo.color,
                backgroundColor: stateInfo.color + '40',
                borderWidth: 3,
                fill: false,
                tension: 0.4,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: stateInfo.color,
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
            };
        }).filter(Boolean);

        const chartData = {
            labels,
            datasets,
        };

        if (report && updateReportCallback) {
            const cacheKey = generateWidgetCacheKey(config, globalFilters);
            const updatedReport = updateWidgetCacheInReport(report, config.id, chartData, cacheKey);
            updateReportCallback(updatedReport);
        }

        return chartData;
    } catch (error) {
        console.error('Error fetching state of emotion by week data:', error);
        throw error;
    }
}

/**
 * Fetch checkin engagement stats data for donut charts
 * Supports two metrics:
 * - Engagement: percentage of students with has_completed_checkin_for_period === true
 * - Ready to Learn: percentage of students with last_checkin_color === "Green"
 */
export async function fetchCheckinEngagementStatsData(
    config,
    globalFilters?,
    report?,
    updateReportCallback?: (updatedReport) => void,
    forceRefresh = false, opts = {}
) {
    if (report && !forceRefresh) {
        const cacheKey = generateWidgetCacheKey(config, globalFilters);
        const cachedData = getWidgetCache(report, config.id, cacheKey, report.metadata?.published || false);
        if (cachedData) {
            const chartData = cachedData.chartData || cachedData;
            return chartData;
        }
    }

    try {
        const { level, interval, term, year, period, levelId } = opts;
        const queryParams = `${level ? `level=${level}` : ''}${interval ? `&interval=${interval}` : ''}${term ? `&term=${term}` : ''}${year ? `&year=${year}` : ''}${period ? `&period=${period}` : ''}${levelId ? `&levelId=${levelId}` : ''}`;

        const response = await dataApi.get(`/api/dynamic-reports/checkin-engagement-stats?${queryParams.toString()}`);

        const apiResponse = response.data;

        if (!apiResponse || !apiResponse.success) {
            throw new Error('Invalid API response format: expected success: true');
        }

        const responseData = apiResponse.data?.processedData;

        if (!Array.isArray(responseData)) {
            throw new Error('Invalid API response format: expected array in data.processedData');
        }

        // Determine metric type based on widget ID or config
        const isEngagementMetric = config.id === 'widget-engagement-donut' ||
            config.id?.includes('engagement');
        const isReadyToLearnMetric = config.id === 'widget-ready-to-learn-donut' ||
            config.id?.includes('ready-to-learn') ||
            config.id?.includes('ready-to-learn-donut');

        let engagedCount = 0;
        let totalCount = responseData.length;

        if (isEngagementMetric) {
            engagedCount = responseData.filter(
                (item) => item.has_completed_checkin_for_period === true
            ).length;
            totalCount = responseData.length;
        } else if (isReadyToLearnMetric) {
            engagedCount = apiResponse.data.rtlPercentage;
            totalCount = 100;
        } else {
            engagedCount = responseData.filter(
                (item) => item.has_completed_checkin_for_period === true
            ).length;
            totalCount = responseData.length;
        }

        const engagedPercentage = totalCount > 0 ? (engagedCount / totalCount) * 100 : 0;
        const notEngagedPercentage = 100 - engagedPercentage;

        const engagedColor = isReadyToLearnMetric
            ? (config.dataSources?.[0]?.color || '#14B8A6')
            : '#E91E63';

        const notEngagedColor = 'lightgrey';

        const engagedLabel = isReadyToLearnMetric ? 'Ready to Learn' : 'Engaged';
        const notEngagedLabel = isReadyToLearnMetric ? 'Other' : 'Not Engaged';

        const chartData = {
            labels: [engagedLabel, notEngagedLabel],
            datasets: [
                {
                    label: isReadyToLearnMetric ? 'Ready to Learn %' : 'Engagement %',
                    data: [engagedPercentage, notEngagedPercentage],
                    backgroundColor: [engagedColor, notEngagedColor],
                    borderColor: [engagedColor, notEngagedColor],
                    borderWidth: 1, // Thin border
                },
            ],
        };

        // Update cache if report is provided
        if (report && updateReportCallback) {
            const cacheKey = generateWidgetCacheKey(config, globalFilters);
            const updatedReport = updateWidgetCacheInReport(report, config.id, chartData, cacheKey);
            updateReportCallback(updatedReport);
        }

        const summary = isReadyToLearnMetric ? `For the current week, ${engagedCount}% of students are ready to learn` : `For the current week, ${engagedPercentage.toFixed(1)}% are engaged.`

        return {
            chartData,
            summary
        };
    } catch (error) {
        console.error('Error fetching checkin engagement stats data:', error);
        throw error;
    }
}

/**
 * Fetch Not Ready to Learn states data for horizontal bar chart
 * @param config Widget configuration
 * @param globalFilters Optional global filters
 * @param report Optional report object for caching
 * @param updateReportCallback Optional callback to update report cache
 * @param forceRefresh Whether to force refresh (skip cache)
 * @returns Chart data with 4 equal-width segments showing percentages
 */
export async function fetchNotReadyToLearnStatesData(
    config,
    globalFilters,
    report,
    updateReportCallback,
    forceRefresh = false, opts = {}
) {
    // Check cache first if report is provided and not forcing refresh
    if (report && !forceRefresh) {
        const cacheKey = generateWidgetCacheKey(config, globalFilters);
        const cachedData = getWidgetCache(report, config.id, cacheKey, (report.metadata && report.metadata.published) || false);
        if (cachedData) {
            const chartData = cachedData.chartData || cachedData;
            return chartData;
        }
    }

    try {
        const { level, interval, term, year, period, levelId } = opts;
        const queryParams = `${level ? `level=${level}` : ''}${interval ? `&interval=${interval}` : ''}${term ? `&term=${term}` : ''}${year ? `&year=${year}` : ''}${period ? `&period=${period}` : ''}${levelId ? `&levelId=${levelId}` : ''}`;



        // Fetch from API
        const response = await dataApi.get(`/api/dynamic-reports/checkin-engagement-stats?${queryParams.toString()}`);

        const apiResponse = response.data;

        if (!apiResponse || !apiResponse.success) {
            throw new Error('Invalid API response format: expected success: true');
        }

        // Extract stateOfEmotionPercentage from response (includes all 5 states)
        const stateOfEmotionPercentage = apiResponse.data && apiResponse.data.uniqueStudentsInEachStateOfEmotion;

        if (!Array.isArray(stateOfEmotionPercentage) || stateOfEmotionPercentage.length === 0) {
            throw new Error('Invalid API response format: expected array in data.stateOfEmotionPercentage');
        }

        // Map state labels to sourceIds for color matching (all 5 states)
        const stateLabelToSourceId = {
            'Ready to learn': 'ready-to-learn',
            'In a state of processing': 'processing',
            'Not quite ready to learn': 'not-ready',
            'Heightened state of emotion': 'heightened-emotion',
            'Emotional overload': 'emotional-overload',
        };

        // Get enabled data sources from widget config
        const enabledSources = (config.dataSources || []).filter((ds) => ds.enabled !== false);

        // Create reverse map: sourceId -> label for easier lookup
        const sourceIdToLabel = {};
        Object.keys(stateLabelToSourceId).forEach((label) => {
            sourceIdToLabel[stateLabelToSourceId[label]] = label;
        });

        // Create a map of label to count from API data for quick lookup
        const apiDataMap = new Map();
        stateOfEmotionPercentage.forEach((zone) => {
            apiDataMap.set(zone.label, zone.count || 0);
        });

        // Transform API data to chart format - iterate over enabled sources to ensure all are included
        // Each segment gets fixed width - ensure they total exactly 100%
        const fixedWidthPerSegment = 20; // 5 segments = 100%
        const datasets = enabledSources.map((source, index, array) => {
            // Find the label for this sourceId
            const stateLabel = sourceIdToLabel[source.sourceId];

            if (!stateLabel) {
                // If no matching label found, skip this source
                console.warn(`No matching label found for sourceId: ${source.sourceId}`);
                return null;
            }

            // Get count from API data, default to 0 if not found
            const count = apiDataMap.get(stateLabel) || 0;

            const sourceColor = source.color || '#14B8A6';
            const baseColor = extractBaseColor(sourceColor);

            // Use fixed width for all segments except the last one
            // Last segment gets the remainder to ensure total is exactly 100
            const isLastSegment = index === array.length - 1;
            let segmentWidth = isLastSegment
                ? 100 - (fixedWidthPerSegment * (array.length - 1))
                : fixedWidthPerSegment;

            // Ensure segment width is at least 0.1 to prevent Chart.js rendering issues
            if (segmentWidth <= 0) {
                console.warn(`[Not Ready Bar] Segment width is ${segmentWidth} for ${stateLabel}, setting to 0.1`);
                segmentWidth = 0.1;
            }

            return {
                label: stateLabel,
                data: [segmentWidth], // Fixed width per segment, last one fills remainder
                backgroundColor: addOpacityToColor(baseColor, 0.85),
                borderColor: addOpacityToColor(baseColor, 1.0),
                stack: 'stack', // All datasets stack together
                displayPercentage: count, // Store count for label display
            };
        }).filter((dataset) => dataset !== null); // Remove any null entries

        // Ensure we have exactly 5 datasets (all states)
        if (datasets.length !== 5) {
            console.warn(`[Not Ready Bar] Expected 5 datasets but got ${datasets.length}:`, datasets.map(d => d.label));
        }

        // Verify green segment is present and has valid data
        let greenSegment = datasets.find(d => d.label === 'Ready to learn');
        if (!greenSegment) {
            console.error('[Not Ready Bar] Green segment (Ready to learn) is MISSING from datasets!');
            console.log('[Not Ready Bar] Available labels:', datasets.map(d => d.label));
        } else if (greenSegment.data[0] <= 0) {
            console.error('[Not Ready Bar] Green segment has invalid width:', greenSegment.data[0]);
            // Fix invalid width
            greenSegment.data[0] = fixedWidthPerSegment;
        } else {
            // Ensure green segment is first in the array for proper Chart.js rendering
            const greenIndex = datasets.indexOf(greenSegment);
            if (greenIndex > 0) {
                console.log(`[Not Ready Bar] Moving green segment from index ${greenIndex} to 0`);
                datasets.splice(greenIndex, 1);
                datasets.unshift(greenSegment);
            }
        }

        const chartData = {
            labels: [''], // Single label for single bar
            datasets: datasets,
        };

        // Update cache if report is provided
        if (report && updateReportCallback) {
            const cacheKey = generateWidgetCacheKey(config, globalFilters);
            const updatedReport = updateWidgetCacheInReport(report, config.id, chartData, cacheKey);
            updateReportCallback(updatedReport);
        }

        return chartData;
    } catch (error) {
        console.error('Error fetching not ready to learn states data:', error);
        throw error;
    }
}

// Helper function to extract base color (same as in mockData.js)
function extractBaseColor(color) {
    if (color.startsWith('rgba')) {
        const match = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+)/);
        if (match) {
            return `rgb(${match[1]}, ${match[2]}, ${match[3]})`;
        }
    }
    if (color.startsWith('hsla')) {
        const match = color.match(/hsla\((\d+),\s*(\d+)%,\s*(\d+)%/);
        if (match) {
            return `hsl(${match[1]}, ${match[2]}%, ${match[3]}%)`;
        }
    }
    return color;
}

// Helper function to add opacity to color (same as in mockData.js)
function addOpacityToColor(color, opacity) {
    if (color.startsWith('rgba') || color.startsWith('rgb')) {
        const match = color.match(/\d+/g);
        if (match && match.length >= 3) {
            return `rgba(${match[0]}, ${match[1]}, ${match[2]}, ${opacity})`;
        }
    }
    if (color.startsWith('#')) {
        const hex = color.slice(1);
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    if (color.startsWith('hsl')) {
        const hslMatch = color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
        if (hslMatch) {
            return `hsla(${hslMatch[1]}, ${hslMatch[2]}%, ${hslMatch[3]}%, ${opacity})`;
        }
        const hslaMatch = color.match(/hsla\((\d+),\s*(\d+)%,\s*(\d+)%,\s*[\d.]+\)/);
        if (hslaMatch) {
            return `hsla(${hslaMatch[1]}, ${hslaMatch[2]}%, ${hslaMatch[3]}%, ${opacity})`;
        }
    }
    return color;
}

/**
 * Fetch State of Emotion state detail (trend data) from API
 * @param stateId The emotional state ID (e.g., "ready-to-learn")
 * @param dateRange Date range configuration from widget
 * @param configPeriod Period for trend data (daily, weekly, monthly, annual) - defaults to "weekly"
 * @returns Raw API response data with trend information
 */
export async function fetchStateOfEmotionStateDetail(
    stateId,
    dateRange,
    configPeriod: 'daily' | 'weekly' | 'monthly' | 'annual' = 'weekly'
) {
    // Extract period and year from dateRange
    let apiPeriod = 'year'; // default
    let year;

    if (dateRange) {
        // Map widget period to API period format
        const periodMap = {
            week: 'week',
            month: 'month',
            term: 'term',
            year: 'year',
            custom: 'year', // For custom, default to year
        };

        apiPeriod = periodMap[dateRange.period] || 'year';

        // Extract year from dateRange or from startDate/endDate
        if (dateRange.year) {
            year = dateRange.year;
        } else if (dateRange.startDate) {
            // Extract year from startDate (YYYY-MM-DD format)
            const startYear = new Date(dateRange.startDate).getFullYear();
            year = startYear;
        } else if (dateRange.endDate) {
            // Extract year from endDate (YYYY-MM-DD format)
            const endYear = new Date(dateRange.endDate).getFullYear();
            year = endYear;
        } else {
            // Default to current year
            year = new Date().getFullYear();
        }
    } else {
        // Default to current year if no dateRange
        year = new Date().getFullYear();
    }

    // Build request body
    // Only include year if period is "year"
    const dateRangeBody = {
        period: apiPeriod,
    };

    if (apiPeriod === 'year' && year) {
        dateRangeBody.year = year;
    }

    const requestBody = {
        dateRange: dateRangeBody,
        config: {
            period: configPeriod,
        },
    };

    try {
        const response = await dataApi.post(`/api/dynamic-reports/state-of-emotion/${stateId}`, requestBody);
        const responseData = response.data;

        if (!responseData.success || !responseData.data) {
            throw new Error('Invalid API response format');
        }

        return responseData.data;
    } catch (error) {
        console.error('Error fetching state detail data:', error);
        throw error;
    }
}

/**
 * Fetch class engagement list from API
 * @param config Widget configuration
 * @param globalFilters Optional global filters to merge with data source filters
 * @returns Raw API response data with list of classes
 */
// Module-level cache for class engagement data
// This allows the main card and drilldown to share the same cached data
// interface CachedClassEngagementListData {
//   data;
//   cacheKey;
// }

const classEngagementListCache = new Map();

/**
 * Generate cache key for class engagement data (exported for drilldown cache lookup)
 */
export function getClassEngagementCacheKey(config, globalFilters?, opts = {}) {
    const widgetId = config.id || '';
    const widgetConfig = config.config;
    const engagementMetric = widgetConfig?.engagementMetric;
    const filtersStr = JSON.stringify(globalFilters || {});
    const configStr = JSON.stringify(engagementMetric || {});
    const optsStr = JSON.stringify({ interval: opts.interval, term: opts.term, year: opts.year });
    return `${widgetId}:${filtersStr}:${configStr}:${optsStr}`;
}

export async function fetchClassEngagementList(config, globalFilters?, forceRefresh = false, opts = {}) {
    // Generate cache key (include opts so cache is per report period)
    const cacheKey = getClassEngagementCacheKey(config, globalFilters, opts);
    const isAISummaryCall = !!config?.aiSummaryEnabled;
    // Check cache first if not forcing refresh
    if (!forceRefresh) {
        const cached = classEngagementListCache.get(cacheKey);
        if (cached && !isAISummaryCall) {
            // Need to check if its ai summary call, otherwise, it returns cached data instead of invoking API
            return cached.data;
        }
    }

    // Build request body
    const widgetConfig = config.config;
    const engagementMetric = widgetConfig?.engagementMetric;

    // Merge report period (interval, term, year) from opts into engagementMetric.period
    // Template has hardcoded period; URL params (opts) contain the actual report week
    const isValidNum = (n) => typeof n === 'number' && !Number.isNaN(n);
    const period = engagementMetric?.period
        ? {
              ...engagementMetric.period,
              ...(isValidNum(opts.interval) && { interval: opts.interval }),
              ...(isValidNum(opts.term) && { term: opts.term }),
              ...(isValidNum(opts.year) && { year: opts.year }),
          }
        : engagementMetric?.period || widgetConfig?.dateRange;

    const effectiveEngagementMetric = engagementMetric ? { ...engagementMetric, period } : engagementMetric;

    // Extract level and levelId from opts
    const { level, levelId } = opts;

    // Build request body
    const requestBody = {
        globalFilters: globalFilters || {},
        dateRange: period,
        config: {
            engagementMetric: effectiveEngagementMetric,
            // Add level and levelId to config next to date range params
            ...(level && { level }),
            ...(levelId && { levelId }),
        },
        aiSummaryEnabled: config?.aiSummaryEnabled || false,
    };

    try {
        const response = await dataApi.post('/api/dynamic-reports/class-engagement', requestBody);
        const responseData = response.data;

        if (!responseData.success || !responseData.data) {
            throw new Error('Invalid API response format');
        }

        // Cache the response (always update cache, even on force refresh)
        classEngagementListCache.set(cacheKey, {
            data: responseData.data,
            cacheKey: cacheKey,
        });

        return responseData.data;
    } catch (error) {
        console.error('Error fetching class engagement list:', error);
        throw error;
    }
}

/**
 * Fetch class engagement detail from API
 * @param classId The class ID
 * @param config Widget configuration
 * @param globalFilters Optional global filters to merge with data source filters
 * @param opts Optional { interval, term, year } to override template period
 * @returns Raw API response data with class detail
 */
export async function fetchClassEngagementDetail(classId, config, globalFilters?, opts = {}) {
    const widgetConfig = config.config;
    const engagementMetric = widgetConfig?.engagementMetric;
    const isValidNum = (n) => typeof n === 'number' && !Number.isNaN(n);
    const period = engagementMetric?.period
        ? {
              ...engagementMetric.period,
              ...(isValidNum(opts.interval) && { interval: opts.interval }),
              ...(isValidNum(opts.term) && { term: opts.term }),
              ...(isValidNum(opts.year) && { year: opts.year }),
          }
        : engagementMetric?.period || widgetConfig?.dateRange;
    const effectiveEngagementMetric = engagementMetric ? { ...engagementMetric, period } : engagementMetric;

    const requestBody = {
        globalFilters: globalFilters || {},
        dateRange: period,
        config: {
            engagementMetric: effectiveEngagementMetric,
        },
    };

    try {
        const response = await dataApi.post(`/api/dynamic-reports/class-engagement/${classId}`, requestBody);

        const responseData = response.data;

        if (!responseData.success || !responseData.data) {
            throw new Error('Invalid API response format');
        }

        return responseData.data;
    } catch (error) {
        console.error(`Error fetching class engagement detail for class ${classId}:`, error);
        throw error;
    }
}

/**
 * Fetch student engagement detail from API
 * @param classId The class ID
 * @param studentId The student ID
 * @param config Widget configuration
 * @param globalFilters Optional global filters to merge with data source filters
 * @param opts Optional { interval, term, year } to override template period
 * @returns Raw API response data with student detail
 */
export async function fetchStudentEngagementDetail(classId, studentId, config, globalFilters?, opts = {}) {
    const widgetConfig = config.config;
    const engagementMetric = widgetConfig?.engagementMetric;
    const isValidNum = (n) => typeof n === 'number' && !Number.isNaN(n);
    const period = engagementMetric?.period
        ? {
              ...engagementMetric.period,
              ...(isValidNum(opts.interval) && { interval: opts.interval }),
              ...(isValidNum(opts.term) && { term: opts.term }),
              ...(isValidNum(opts.year) && { year: opts.year }),
          }
        : engagementMetric?.period || widgetConfig?.dateRange;
    const effectiveEngagementMetric = engagementMetric ? { ...engagementMetric, period } : engagementMetric;

    const requestBody = {
        globalFilters: globalFilters || {},
        dateRange: period,
        config: {
            engagementMetric: effectiveEngagementMetric,
        },
    };

    try {
        const response = await dataApi.post(
            `/api/dynamic-reports/class-engagement/${classId}/${studentId}`,
            requestBody
        );

        const responseData = response.data;

        if (!responseData.success || !responseData.data) {
            throw new Error('Invalid API response format');
        }

        return responseData.data;
    } catch (error) {
        console.error(`Error fetching student engagement detail for student ${studentId} in class ${classId}:`, error);
        throw error;
    }
}

/**
 * Fetch class engagement data from API for the main card view
 * @param config Widget configuration
 * @param globalFilters Optional global filters to merge with data source filters
 * @returns ChartData formatted for bar chart display
 */
// Export function to populate drilldown cache from main card data
// This allows the drilldown to use data already loaded by the main card
export function populateClassEngagementDrillDownCache(cacheKey, rawClasses) {
    // Transform raw classes to ClassEngagementListItem format
    const classes = rawClasses.map((item) => ({
        classId: item.classId,
        className: item.className,
        engagementPercentage: typeof item.engagement === 'string' ? parseFloat(item.engagement) : item.engagement || 0,
        studentCount: item.totalStudents || 0,
    }));

    // Sort by class name alphabetically
    classes.sort((a, b) => {
        const nameA = (a.className || `Class ${a.classId}`).toLowerCase();
        const nameB = (b.className || `Class ${b.classId}`).toLowerCase();
        return nameA.localeCompare(nameB);
    });

    // Store in a module-level cache that the drilldown can access
    // We'll import this in the drilldown component
    if (typeof window !== 'undefined') {
        window.__classEngagementDrillDownCache = window.__classEngagementDrillDownCache || new Map();
        window.__classEngagementDrillDownCache.set(cacheKey, {
            allClasses: classes,
            rawClassesData: rawClasses,
            cacheKey: cacheKey,
        });
    }
}

export async function fetchClassEngagementData({
    config,
    globalFilters,
    report,
    updateReportCallback,
    forceRefresh = false,
    opts = {}
}) {
    // Check cache first if report is provided and not forcing refresh
    if (report && !forceRefresh) {
        const cacheKey = generateWidgetCacheKey(config, globalFilters);
        const cachedData = getWidgetCache(report, config.id, cacheKey, report.metadata?.published || false);
        if (cachedData) {
            // Populate drilldown cache from report cache
            const cacheKeyForDrilldown = getClassEngagementCacheKey(config, globalFilters, opts);
            if (cachedData.rawClassesData) {
                populateClassEngagementDrillDownCache(cacheKeyForDrilldown, cachedData.rawClassesData);
            }
            // Return chartData if nested, otherwise return cachedData directly
            const chartData = cachedData.chartData || cachedData;
            return chartData;
        }
    }

    try {
        // Fetch class engagement list from API (bypass cache if forcing refresh)
        const apiData = await fetchClassEngagementList(config, globalFilters, forceRefresh, opts);

        // Cache the raw data for drilldown use
        const cacheKey = getClassEngagementCacheKey(config, globalFilters, opts);
        const rawClasses = apiData.classes || [];

        // Populate drilldown cache so it can use this data immediately
        if (!config?.aiSummaryEnabled) populateClassEngagementDrillDownCache(cacheKey, rawClasses);

        // Transform API response to ChartData format
        // API format: { classes: [{ classId, className, sisId, engagement, totalWindows, totalStudents }, ...] }
        const classes = rawClasses;

        // Sort by class name alphabetically
        const sortedClasses = [...classes].sort((a, b) => {
            const nameA = (a.className || `Class ${a.classId}`).toLowerCase();
            const nameB = (b.className || `Class ${b.classId}`).toLowerCase();
            return nameA.localeCompare(nameB);
        });

        const labels = sortedClasses.map((item) => item.className || `Class ${item.classId}`);
        const data = sortedClasses.map((item) => {
            const engagement = typeof item.engagement === 'string' ? parseFloat(item.engagement) : item.engagement || 0;
            return engagement;
        });

        const chartData = {
            labels,
            datasets: [
                {
                    label: 'Engagement %',
                    data,
                    backgroundColor: 'rgba(76, 175, 80, 0.6)',
                    borderColor: 'rgba(76, 175, 80, 1)',
                    borderWidth: 2,
                    tension: 0, // Not used for bar charts, but required by ChartData type
                },
            ],
        };

        // Update cache if report is provided
        if (report && updateReportCallback) {
            const cacheKey = generateWidgetCacheKey(config, globalFilters);
            const updatedReport = updateWidgetCacheInReport(
                report,
                config.id,
                { chartData, rawClassesData: rawClasses },
                cacheKey
            );
            updateReportCallback(updatedReport);

            // Auto-save template if this report was loaded from a template (fire and forget)
            import('./templateManager').then(({ autoSaveTemplateIfNeeded }) => {
                autoSaveTemplateIfNeeded(updatedReport).catch((err) => {
                    console.warn('Auto-save template failed:', err);
                });
            });
        }

        if (config?.aiSummaryEnabled && apiData) {
            return {
                aiSummary: apiData,
            };
        }

        return chartData;
    } catch (error) {
        console.error('Error fetching class engagement data:', error);
        // Re-throw error instead of returning mock data
        throw error;
    }
}

/**
 * Module-level cache for grade engagement list (similar to class engagement)
 */
const gradeEngagementListCache = new Map();

/**
 * Generate cache key for grade engagement list (include opts so cache is per report period)
 */
function getGradeEngagementCacheKey(config, globalFilters?, opts = {}) {
    const widgetConfig = config.config;
    const engagementMetric = widgetConfig?.engagementMetric;
    const dateRange = engagementMetric?.period || widgetConfig?.dateRange;
    const optsStr = JSON.stringify({ interval: opts.interval, term: opts.term, year: opts.year });

    return `grade-engagement-${config.id}-${JSON.stringify(globalFilters || {})}-${JSON.stringify(
        dateRange || {}
    )}-${JSON.stringify(engagementMetric || {})}-${optsStr}`;
}

/**
 * Fetch grade engagement list from API
 * @param config Widget configuration
 * @param globalFilters Optional global filters to merge with data source filters
 * @param forceRefresh Whether to bypass cache and force refresh
 * @param opts Optional parameters (level, levelId, period, interval, term, year)
 * @returns Raw API response data with grades list
 */
export async function fetchGradeEngagementList({config, globalFilters, forceRefresh = false, opts = {}}) {
    // Generate cache key (include opts so cache is per report period)
    const cacheKey = getGradeEngagementCacheKey(config, globalFilters, opts);
    // check if its a summary call or not
    const isAISummaryCall = !!config?.aiSummaryEnabled;

    // Check cache first if not forcing refresh
    if (!forceRefresh) {
        const cached = gradeEngagementListCache.get(cacheKey);

        if (cached && !isAISummaryCall) {
            return cached.data;
        }
    }

    // Build request body
    const widgetConfig = config.config;
    const engagementMetric = widgetConfig?.engagementMetric;

    // Extract dateRange from engagementMetric or widget config
    const dateRangeConfig = engagementMetric?.period || widgetConfig?.dateRange || { period: 'year', year: new Date().getFullYear() };

    // Extract level, levelId, period, interval, term, and year from opts
    const { level, levelId, period, interval, term, year } = opts;

    // Use period from opts if provided, otherwise fall back to dateRangeConfig
    // Keep period as "weekly" or "fortnightly" to match URL format
    const effectivePeriod = period || dateRangeConfig.period || dateRangeConfig.type || 'year';
    const effectiveYear = year || dateRangeConfig.year || new Date().getFullYear();
    const effectiveInterval = interval !== undefined ? interval : dateRangeConfig.interval;
    const effectiveTerm = term !== undefined ? term : dateRangeConfig.term;

    // Build dateRange object - use period as-is from URL (weekly/fortnightly) to match URL format
    const dateRangeForConfig = {
        type: effectivePeriod, // Keep as "weekly" or "fortnightly" to match URL
        year: effectiveYear,
        // Include interval and term for fortnightly/weekly periods
        ...(effectivePeriod === 'fortnightly' || effectivePeriod === 'weekly' ? {
            ...(effectiveInterval !== undefined && { interval: effectiveInterval }),
            ...(effectiveTerm !== undefined && { term: effectiveTerm }),
        } : {}),
    };

    // For grade engagement, ensure window config is correct based on period:
    // - window.unit should be "week" for weekly, "fortnight" for fortnightly
    // - window.alignment should be "term" for term alignment, "week" for weekly, "fortnight" for fortnightly
    let correctedEngagementMetric = engagementMetric ? { ...engagementMetric } : undefined;
    if (correctedEngagementMetric && correctedEngagementMetric.window) {
        correctedEngagementMetric = {
            ...correctedEngagementMetric,
            window: {
                ...correctedEngagementMetric.window,
                unit: effectivePeriod === 'fortnightly' ? 'fortnight' : 'week',
                alignment: effectivePeriod === 'fortnightly' ? 'fortnight' : effectivePeriod === 'weekly' ? 'week' : 'term',
            },
        };
    }

    // Build request body
    const requestBody = {
        globalFilters: globalFilters || {},
        dateRange: dateRangeForConfig,
        config: {
            widget: {
                ...config,
                config: {
                    ...widgetConfig,
                    // Add dateRange to config.widget.config - must match URL params
                    dateRange: dateRangeForConfig,
                },
            },
            engagementMetric: correctedEngagementMetric || engagementMetric,
            // Add level and levelId to config next to date range params
            ...(level && { level }),
            ...(levelId && { levelId }),
        },
        aiSummaryEnabled: config?.aiSummaryEnabled || false,
    };


    try {


        const response = await dataApi.post('/api/dynamic-reports/grade-engagement', requestBody);

        const responseData = response.data;

        if (!responseData.success || !responseData.data) {
            throw new Error('Invalid API response format');
        }

        // Cache the response (always update cache, even on force refresh)
        // UPDATE: Because this can return only the summary, we want to ensure,
        //         that we also spread previous cache i.e. gradeDrillDown data
        const existing = gradeEngagementListCache.get(cacheKey);
        gradeEngagementListCache.set(cacheKey, {
            ...existing,
            data: {
                ...(existing?.data ?? {}),
                ...(responseData?.data ?? {}),
            },
            cacheKey,
        });

        return responseData.data;
    } catch (error) {
        console.error('Error fetching grade engagement list:', error);
        throw error;
    }
}

/**
 * Fetch grade engagement students data
 * @param config Widget configuration
 * @param globalFilters Optional global filters
 * @param gradeIds Array of grade IDs to fetch students for
 * @returns API response with students data
 */
export async function fetchGradeEngagementStudents(config, globalFilters?, gradeIds?) {
    try {
        // Build request body
        const widgetConfig = config.config;
        const engagementMetric = widgetConfig?.engagementMetric;

        // Extract dateRange from engagementMetric or widget config
        let dateRange = engagementMetric?.period || widgetConfig?.dateRange;

        // If dateRange is an object with type and year, use it directly
        // Otherwise, construct from globalFilters
        if (!dateRange || !dateRange.type) {
            dateRange = {
                type: globalFilters?.period || 'year',
                year: new Date().getFullYear(),
            };
        }

        // Build request body
        const requestBody = {
            gradeIds: gradeIds || [],
            dateRange: dateRange,
        };



        const response = await dataApi.post('/api/dynamic-reports/grade-engagement/students', requestBody);

        const data = response.data;

        if (!data.success) {
            throw new Error(data.message || 'Failed to fetch grade engagement students');
        }

        return data.data;
    } catch (error) {
        console.error('Error fetching grade engagement students:', error);
        throw error;
    }
}

/**
 * Fetch grade engagement data from API for the main card view
 * @param config Widget configuration
 * @param globalFilters Optional global filters to merge with data source filters
 * @param report Optional report config for caching
 * @param updateReportCallback Optional callback to update report with cache
 * @param forceRefresh Whether to bypass cache and force refresh
 * @param opts Optional parameters (level, levelId, period, interval, term, year)
 * @returns ChartData formatted for bar chart display
 */
export async function fetchGradeEngagementData(
    config,
    globalFilters,
    report,
    updateReportCallback,
    forceRefresh = false,
    opts = {}
) {
    // Check cache first if report is provided and not forcing refresh
    if (report && !forceRefresh) {
        const cacheKey = generateWidgetCacheKey(config, globalFilters);
        const cachedData = getWidgetCache(report, config.id, cacheKey, report.metadata?.published || false);
        if (cachedData) {
            // Return chartData if nested, otherwise return cachedData directly
            const chartData = cachedData.chartData || cachedData;
            return chartData;
        }
    }

    try {
        // Fetch grade engagement list from API (bypass cache if forcing refresh)
        const apiData = await fetchGradeEngagementList(config, globalFilters, forceRefresh, opts);

        if (config?.aiSummaryEnabled && apiData?.aiSummary) {
            return {
                aiSummary: apiData?.aiSummary,
            };
        }

        // Transform API response to ChartData format
        // API format: { grades: [{ gradeId, gradeName, engagement, totalWindows, totalStudents }, ...] }
        const grades = apiData.grades || [];

        // Sort by grade name alphabetically
        const sortedGrades = [...grades].sort((a, b) => {
            const nameA = (a.gradeName || `Grade ${a.gradeId}`).toLowerCase();
            const nameB = (b.gradeName || `Grade ${b.gradeId}`).toLowerCase();
            return nameA.localeCompare(nameB);
        });

        const labels = sortedGrades.map((item) => item.gradeName || `Grade ${item.gradeId}`);
        const data = sortedGrades.map((item) => {
            const engagement = typeof item.engagement === 'string' ? parseFloat(item.engagement) : item.engagement || 0;
            return engagement;
        });

        const chartData = {
            labels,
            datasets: [
                {
                    label: 'Engagement %',
                    data,
                    backgroundColor: 'rgba(76, 175, 80, 0.6)',
                    borderColor: 'rgba(76, 175, 80, 1)',
                    borderWidth: 2,
                    tension: 0, // Not used for bar charts, but required by ChartData type
                },
            ],
        };

        // Update cache if report is provided
        if (report && updateReportCallback) {
            const cacheKey = generateWidgetCacheKey(config, globalFilters);
            const updatedReport = updateWidgetCacheInReport(
                report,
                config.id,
                { chartData, rawGradesData: grades },
                cacheKey
            );
            updateReportCallback(updatedReport);

            // Auto-save template if this report was loaded from a template (fire and forget)
            import('./templateManager').then(({ autoSaveTemplateIfNeeded }) => {
                autoSaveTemplateIfNeeded(updatedReport).catch((err) => {
                    console.warn('Auto-save template failed:', err);
                });
            });
        }

        return chartData;
    } catch (error) {
        console.error('Error fetching grade engagement data:', error);
        // Re-throw error instead of returning mock data
        throw error;
    }
}

/**
 * Fetch check-in emotions data from API
 * @param config Widget configuration
 * @param globalFilters Optional global filters
 * @param forceRefresh Whether to force refresh (bypass cache)
 * @param opts Optional parameters including level, levelId, interval, term, year, period
 * @returns Raw API response data with top emotions and counts
 */
export async function fetchCheckInEmotions({config, globalFilters, forceRefresh = false, opts = {}}) {
    const widgetConfig = config.config;
    const checkInEmotionsConfig = widgetConfig?.checkInEmotions || {};

    // Extract dateRange from widget config
    const dateRangeConfig = widgetConfig?.dateRange || { period: 'year', year: new Date().getFullYear() };

    // Extract level, levelId, period, interval, term, and year from opts
    const { level, levelId, period, interval, term, year } = opts;

    // Use period from opts if provided, otherwise fall back to dateRangeConfig
    // Keep period as "weekly" or "fortnightly" to match URL format
    const effectivePeriod = period || dateRangeConfig.period || 'year';
    const effectiveYear = year || dateRangeConfig.year || new Date().getFullYear();
    const effectiveInterval = interval !== undefined ? interval : dateRangeConfig.interval;
    const effectiveTerm = term !== undefined ? term : dateRangeConfig.term;

    // Build dateRange object - use period as-is from URL (weekly/fortnightly) to match URL format
    const dateRangeForConfig = {
        type: effectivePeriod, // Keep as "weekly" or "fortnightly" to match URL
        year: effectiveYear,
        // Include interval and term for fortnightly/weekly periods
        ...(effectivePeriod === 'fortnightly' || effectivePeriod === 'weekly' ? {
            ...(effectiveInterval !== undefined && { interval: effectiveInterval }),
            ...(effectiveTerm !== undefined && { term: effectiveTerm }),
        } : {}),
    };

    // Build request body in the new format
    const requestBody = {
        dateRange: dateRangeForConfig, // Top level dateRange matches URL params
        config: {
            widget: {
                ...config,
                config: {
                    ...widgetConfig,
                    // Add dateRange to config.widget.config - must match URL params
                    dateRange: dateRangeForConfig,
                },
            },
            window: {
                unit:
                    checkInEmotionsConfig.aggregationWindow === 'term'
                        ? 'week'
                        : effectivePeriod === 'fortnightly'
                            ? 'fortnight'
                            : checkInEmotionsConfig.aggregationWindow || 'week',
                size: 1,
                alignment:
                    checkInEmotionsConfig.aggregationWindow === 'term'
                        ? 'term'
                        : effectivePeriod === 'fortnightly'
                            ? 'fortnight'
                            : effectivePeriod === 'weekly'
                                ? 'week'
                                : checkInEmotionsConfig.aggregationWindow || 'term',
            },
            limit: checkInEmotionsConfig.topN || 10,
            globalFilters: globalFilters || {},
            // Add level and levelId to config next to date range params
            ...(level && { level }),
            ...(levelId && { levelId }),
        },
        aiSummaryEnabled: config?.aiSummaryEnabled || false,
    };

    try {

        const response = await dataApi.post('/api/dynamic-reports/check-in-emotions', requestBody);

        const responseData = response.data;

        if (!responseData.success || !responseData.data) {
            throw new Error('Invalid API response format');
        }

        return responseData.data;
    } catch (error) {
        console.error('Error fetching check-in emotions:', error);
        throw error;
    }
}


/**
 * Fetch student context data from API
 * @param config Widget configuration
 * @param globalFilters Optional global filters
 * @param report Optional report config for cache checking/updating
 * @param updateReportCallback Optional callback to update report with cached data
 * @param forceRefresh Whether to force refresh (bypass cache)
 * @returns ChartData formatted for bar chart
 */
export async function fetchStudentContextData({
    config,
    globalFilters,
    report,
    updateReportCallback,
    forceRefresh = false,
    opts = {}
}) {
    // Check cache first if report is provided and not forcing refresh
    if (report && !forceRefresh) {
        const cacheKey = generateWidgetCacheKey(config, globalFilters);
        const cachedData = getWidgetCache(report, config.id, cacheKey, report.metadata?.published || false);
        if (cachedData) {
            // Return chartData if nested, otherwise return cachedData directly
            const chartData = cachedData.chartData || cachedData;
            return chartData;
        }
    }

    try {
        // Build request body
        const widgetConfig = config.config;
        const dateRangeConfig = widgetConfig?.dateRange || { period: 'week' };

        // Extract level, levelId, period, interval, term, and year from opts
        const { level, levelId, period, interval, term, year } = opts;

        // Use period from opts if provided, otherwise fall back to dateRangeConfig
        const effectivePeriod = period || dateRangeConfig.period || dateRangeConfig.type || 'week';
        const effectiveYear = year || dateRangeConfig.year || new Date().getFullYear();
        const effectiveInterval = interval !== undefined ? interval : dateRangeConfig.interval;
        const effectiveTerm = term !== undefined ? term : dateRangeConfig.term;

        // Build dateRange object - use period as-is from URL (weekly/fortnightly) to match URL format
        const dateRangeForConfig = {
            type: effectivePeriod, // Keep as "weekly" or "fortnightly" to match URL
            year: effectiveYear,
            // Include interval and term for fortnightly/weekly periods
            ...(effectivePeriod === 'fortnightly' || effectivePeriod === 'weekly' ? {
                ...(effectiveInterval !== undefined && { interval: effectiveInterval }),
                ...(effectiveTerm !== undefined && { term: effectiveTerm }),
            } : {}),
        };

        const requestBody = {
            dateRange: dateRangeForConfig,
            globalFilters: globalFilters || {},
            config: {
                // Add level and levelId to config next to date range params
                ...(level && { level }),
                ...(levelId && { levelId }),
            },
            aiSummaryEnabled: config?.aiSummaryEnabled,
        };


        const response = await dataApi.post('/api/dynamic-reports/student-context', requestBody);

        const responseData = response.data;

        if (!responseData.success || !responseData.data) {
            throw new Error('Invalid API response format');
        }

        if (config?.aiSummaryEnabled && responseData?.data?.aiSummary) {
            return {
                aiSummary: responseData?.data?.aiSummary,
            };
        }

        // Transform API response to ChartData format
        // API format: { contexts: [{ contextId, contextName, emotions: { [emotionId] } }, ...] }
        const apiData = responseData.data;
        const contexts = apiData.contexts || [];

        // Get all unique emotion IDs from all contexts
        const emotionIds = new Set();
        contexts.forEach((context) => {
            if (context.emotions) {
                Object.keys(context.emotions).forEach((emotionId) => emotionIds.add(emotionId));
            }
        });

        // Map emotion IDs to labels and colors (matching the predesigned card registry)
        const emotionMap = {
            'ready-to-learn': { label: 'Ready to Learn', color: '#14B8A6' }, // Teal
            processing: { label: 'In a state of processing', color: '#EAB308' }, // Mustard
            'not-ready': { label: 'Not Ready to Learn', color: '#2196F3' }, // Blue
            'heightened-emotion': { label: 'Heightened State of Emotion', color: '#9C27B0' }, // Purple
            'emotional-overload': { label: 'Emotional Overload', color: '#F44336' }, // Red
        };

        // Build labels (context names - use label field from API)
        const labels = contexts.map(
            (context) => context.label || context.contextName || context.contextId || 'Unknown'
        );

        // Build datasets (one per emotion)
        const datasets = Array.from(emotionIds).map((emotionId) => {
            const emotionInfo = emotionMap[emotionId] || { label: emotionId, color: '#666' };
            const data = contexts.map((context) => {
                return context.emotions?.[emotionId] || 0;
            });

            return {
                label: emotionInfo.label,
                data,
                backgroundColor: emotionInfo.color,
                borderColor: emotionInfo.color,
                borderWidth: 1,
            };
        });

        const chartData = {
            labels,
            datasets,
        };

        // Update cache in report if provided
        if (report && updateReportCallback) {
            const cacheKey = generateWidgetCacheKey(config, globalFilters);
            const updatedReport = updateWidgetCacheInReport(report, config.id, { chartData }, cacheKey);
            updateReportCallback(updatedReport);
        }

        return chartData;
    } catch (error) {
        console.error('Error fetching student context data:', error);
        // Re-throw error instead of returning mock data
        throw error;
    }
}

/**
 * Fetch student context raw data from API (for drill-down)
 * @param config Widget configuration
 * @param globalFilters Optional global filters
 * @returns Raw API response data with contexts and emotions
 */
export async function fetchStudentContextRawData(config, globalFilters?, opts = {}) {
    // Build request body
    const widgetConfig = config.config;
    const dateRangeConfig = widgetConfig?.dateRange || { period: 'week' };

    // Extract level, levelId, period, interval, term, and year from opts
    const { level, levelId, period, interval, term, year } = opts;

    // Use period from opts if provided, otherwise fall back to dateRangeConfig
    const effectivePeriod = period || dateRangeConfig.period || dateRangeConfig.type || 'week';
    const effectiveYear = year || dateRangeConfig.year || new Date().getFullYear();
    const effectiveInterval = interval !== undefined ? interval : dateRangeConfig.interval;
    const effectiveTerm = term !== undefined ? term : dateRangeConfig.term;

    // Build dateRange object - use period as-is from URL (weekly/fortnightly) to match URL format
    const dateRangeForConfig = {
        type: effectivePeriod, // Keep as "weekly" or "fortnightly" to match URL
        year: effectiveYear,
        // Include interval and term for fortnightly/weekly periods
        ...(effectivePeriod === 'fortnightly' || effectivePeriod === 'weekly' || effectivePeriod === 'week' ? {
            ...(effectiveInterval !== undefined && { interval: effectiveInterval }),
            ...(effectiveTerm !== undefined && { term: effectiveTerm }),
        } : {}),
    };

    const requestBody = {
        dateRange: dateRangeForConfig,
        globalFilters: globalFilters || {},
        cumulative: true, // Drill-down should show cumulative year-to-date data
    };

    try {
        const response = await dataApi.post('/api/dynamic-reports/student-context', requestBody);

        const responseData = response.data;

        if (!responseData.success || !responseData.data) {
            throw new Error('Invalid API response format');
        }

        return responseData.data;
    } catch (error) {
        console.error('Error fetching student context raw data:', error);
        throw error;
    }
}

/**
 * Fetch context detail data from API
 * @param contextId The context ID (e.g., "at-home")
 * @param config Widget configuration
 * @param globalFilters Optional global filters
 * @param dateRange Optional date range override
 * @returns Raw API response data with context detail
 */
export async function fetchContextDetail(contextId, config, globalFilters?, dateRange?, opts = {}) {
    // Build request body
    const widgetConfig = config.config;
    const dateRangeConfig = dateRange || widgetConfig?.dateRange || { period: 'week' };

    // Extract level, levelId, period, interval, term, and year from opts
    const { level, levelId, period, interval, term, year } = opts;

    // Use period from opts if provided, otherwise fall back to dateRangeConfig
    const effectivePeriod = period || dateRangeConfig.period || dateRangeConfig.type || 'week';
    const effectiveYear = year || dateRangeConfig.year || new Date().getFullYear();
    const effectiveInterval = interval !== undefined ? interval : dateRangeConfig.interval;
    const effectiveTerm = term !== undefined ? term : dateRangeConfig.term;

    // Build dateRange object - use period as-is from URL (weekly/fortnightly) to match URL format
    const effectiveDateRange = {
        type: effectivePeriod, // Keep as "weekly" or "fortnightly" to match URL
        year: effectiveYear,
        // Include interval and term for fortnightly/weekly periods
        ...(effectivePeriod === 'fortnightly' || effectivePeriod === 'weekly' || effectivePeriod === 'week' ? {
            ...(effectiveInterval !== undefined && { interval: effectiveInterval }),
            ...(effectiveTerm !== undefined && { term: effectiveTerm }),
        } : {}),
    };

    const requestBody = {
        contextId: contextId,
        dateRange: effectiveDateRange,
        globalFilters: globalFilters || {},
        cumulative: true, // Drill-down should show cumulative year-to-date data
    };

    try {
        const response = await dataApi.post('/api/dynamic-reports/student-context/detail', requestBody);

        const responseData = response.data;

        if (!responseData.success || !responseData.data) {
            throw new Error('Invalid API response format');
        }

        return responseData.data;
    } catch (error) {
        console.error('Error fetching context detail:', error);
        throw error;
    }
}

/**
 * Fetch context state detail data from API
 * @param contextId The context ID (e.g., "at-home")
 * @param stateId The emotional state ID (e.g., "ready-to-learn")
 * @param config Widget configuration
 * @param globalFilters Optional global filters
 * @param dateRange Optional date range override
 * @returns Raw API response data with context state detail
 */
export async function fetchContextStateDetail(contextId, stateId, config, globalFilters?, dateRange?) {
    // Build request body
    const widgetConfig = config.config;
    const effectiveDateRange = dateRange || widgetConfig?.dateRange || { period: 'week' };

    const requestBody = {
        contextId: contextId,
        stateId: stateId,
        dateRange: effectiveDateRange,
        globalFilters: globalFilters || {},
    };

    try {
        const response = await dataApi.post('/api/dynamic-reports/student-context/detail', requestBody);

        const responseData = response.data;

        if (!responseData.success || !responseData.data) {
            throw new Error('Invalid API response format');
        }

        return responseData.data;
    } catch (error) {
        console.error('Error fetching context state detail:', error);
        throw error;
    }
}

/**
 * Hook to fetch widget data from API
 * TODO: Replace with actual API call: GET /api/widget-data
 */
export function useFetchWidgetData(config) {
    return useMemo(() => {
        // For now, return mock data directly
        switch (config.type) {
            case 'metric':
                return generateMockMetricData(config);
            case 'line-chart':
            case 'bar-chart':
                return generateMockChartData(config);
            case 'table':
                return generateMockTableData(config);
            case 'insight':
                return generateMockInsightData(config);
            default:
                return null;
        }
    }, [config]);
}

/**
 * Fetch insights data from API
 * @param globalFilters Optional global filters
 * @param dateRange Optional date range override
 * @returns Raw API response data with insights
 */
export async function fetchInsights(globalFilters?, dateRange?) {
    // Build request body
    const requestBody = {
        dateRange: dateRange || { period: 'year', year: new Date().getFullYear() },
        globalFilters: globalFilters || {},
    };


    try {
        const response = await dataApi.post('/api/dynamic-reports/insights', requestBody);

        const responseData = response.data;

        if (!responseData.success || !responseData.data) {
            throw new Error('Invalid API response format');
        }

        return responseData.data;
    } catch (error) {
        console.error('Error fetching insights:', error);
        throw error;
    }
}

/**
 * Fetch State of Emotion by Day of Week data for stacked bar chart
 * @param config Widget configuration
 * @param globalFilters Optional global filters
 * @param report Optional report object for caching
 * @param updateReportCallback Optional callback to update report cache
 * @param forceRefresh Whether to force refresh (skip cache)
 * @returns Chart data with days on x-axis and stacked bars for each emotional state
 */
export async function fetchStateOfEmotionByDayData(
    config,
    globalFilters,
    report,
    updateReportCallback,
    forceRefresh = false, opts = {}
) {
    // Check cache first if report is provided and not forcing refresh
    if (report && !forceRefresh) {
        const cacheKey = generateWidgetCacheKey(config, globalFilters);
        const cachedData = getWidgetCache(report, config.id, cacheKey, (report.metadata && report.metadata.published) || false);
        if (cachedData) {
            const chartData = cachedData.chartData || cachedData;
            return chartData;
        }
    }

    try {
        const { level, interval, term, year, period, levelId } = opts;
        const queryParams = `${level ? `level=${level}` : ''}${interval ? `&interval=${interval}` : ''}${term ? `&term=${term}` : ''}${year ? `&year=${year}` : ''}${period ? `&period=${period}` : ''}${levelId ? `&levelId=${levelId}` : ''}`;

        // Fetch from API
        const response = await dataApi.get(`/api/dynamic-reports/state-of-emotion-by-day?${queryParams.toString()}`);

        const apiResponse = response.data;

        if (!apiResponse || !apiResponse.success) {
            throw new Error('Invalid API response format: expected success: true');
        }

        // Extract day data from response
        const dayData = apiResponse.data && apiResponse.data;

        if (!Array.isArray(dayData) || dayData.length === 0) {
            throw new Error('Invalid API response format: expected array in data');
        }

        // Map state labels to sourceIds for color matching
        const stateLabelToSourceId = {
            'Ready to learn': 'ready-to-learn',
            'In a state of processing': 'processing',
            'Not quite ready to learn': 'not-ready',
            'Heightened state of emotion': 'heightened-emotion',
            'Emotional overload': 'emotional-overload',
        };

        // Get enabled data sources from widget config
        const enabledSources = (config.dataSources || []).filter((ds) => ds.enabled !== false);

        // Extract labels (days of the week)
        const labels = dayData.map((day) => day.day);

        // Create a map of stateOfEmotion to dataset index
        const stateToIndex = {};
        const datasets = [];

        // Initialize datasets for each emotional state
        enabledSources.forEach((source) => {
            const stateLabel = Object.keys(stateLabelToSourceId).find(
                (label) => stateLabelToSourceId[label] === source.sourceId
            );
            if (stateLabel) {
                const index = datasets.length;
                stateToIndex[stateLabel] = index;
                datasets.push({
                    label: stateLabel,
                    data: [],
                    backgroundColor: addOpacityToColor(extractBaseColor(source.color || '#14B8A6'), 0.85),
                    borderColor: addOpacityToColor(extractBaseColor(source.color || '#14B8A6'), 1.0),
                    stack: 'stack',
                });
            }
        });

        // Populate data for each day
        dayData.forEach((day) => {
            const dayPercentages = day.dayDataByZorPercentage || [];

            // Initialize all datasets with 0 or null for this day
            datasets.forEach((dataset) => {
                dataset.data.push(0);
            });

            // Fill in actual percentages
            dayPercentages.forEach((emotionData) => {
                const stateLabel = emotionData.stateOfEmotion;
                const index = stateToIndex[stateLabel];
                if (index !== undefined) {
                    const percentage = emotionData.percentage !== null && emotionData.percentage !== undefined
                        ? parseFloat(emotionData.percentage.toFixed(2))
                        : 0;
                    datasets[index].data[datasets[index].data.length - 1] = percentage;
                }
            });
        });

        const chartData = {
            labels: labels,
            datasets: datasets,
        };

        // Update cache if report is provided
        if (report && updateReportCallback) {
            const cacheKey = generateWidgetCacheKey(config, globalFilters);
            const updatedReport = updateWidgetCacheInReport(report, config.id, chartData, cacheKey);
            updateReportCallback(updatedReport);
        }

        return chartData;
    } catch (error) {
        console.error('Error fetching state of emotion by day data:', error);
        throw error;
    }
}

/**
 * Fetch State of Emotion by Day of Week data for stacked bar chart
 * @param config Widget configuration
 * @param globalFilters Optional global filters
 * @param report Optional report object for caching
 * @param updateReportCallback Optional callback to update report cache
 * @param forceRefresh Whether to force refresh (skip cache)
 * @returns Chart data with days on x-axis and stacked bars for each emotional state
 */
export async function fetchStateOfEmotionByDayAISummary(
    config,
    globalFilters,
    report,
    updateReportCallback,
    forceRefresh = false
) {
    // Check cache first if report is provided and not forcing refresh
    if (report && !forceRefresh) {
        const cacheKey = generateWidgetCacheKey(config, globalFilters);
        const cachedData = getWidgetCache(report, config.id, cacheKey, (report.metadata && report.metadata.published) || false);
        if (cachedData) {
            const chartData = cachedData.chartData || cachedData;
            return chartData;
        }
    }

    try {
        // Build query parameters from dateRange config
        const dateRange = (config.config && config.config.dateRange) || { type: 'week', year: 2026, interval: 1, term: 1 };
        const level = (globalFilters && globalFilters.level) || 'school';
        const levelId = globalFilters && globalFilters.levelId;

        const queryParams = new URLSearchParams({
            level: level,
            period: 'weekly',
            interval: String(dateRange.interval || 1),
            term: String(dateRange.term || 1),
            year: String(dateRange.year || 2026),
        });

        if (levelId) {
            queryParams.append('levelId', String(levelId));
        }

        // Fetch from API
        const response = await dataApi.get(`/api/generate-ai-summaries/state-of-emotion-by-day?${queryParams.toString()}`);

        const apiResponse = response.data;

        if (!apiResponse || !apiResponse.success) {
            throw new Error('Invalid API response format: expected success: true');
        }

        const aiSummary = {
            summary: "TO BE GENERATED"
        }

        return aiSummary;
    } catch (error) {
        console.error('Error fetching state of emotion by day data:', error);
        throw error;
    }
}



